'use client';

import React from 'react';
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

interface SettlementSummaryChartProps {
  salesValue: number;
  expenses: number;
  commission: number;
  sent: number;
}

export default function SettlementSummaryChart({
  salesValue,
  expenses,
  commission,
  sent,
}: SettlementSummaryChartProps) {
  const net = salesValue - expenses - commission - sent;

  const data = [
    { name: 'Sales Value', value: salesValue, fill: 'var(--accent)' },
    { name: 'Sent to Dist.', value: sent, fill: 'var(--info)' },
    { name: 'Expenses', value: expenses, fill: 'var(--danger)' },
    { name: 'Commission', value: commission, fill: 'var(--primary)' },
  ];

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: { name: string; value: number }[] }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-xl p-3 shadow-xl">
          <p className="text-xs font-semibold text-foreground">{payload[0].name}</p>
          <p className="text-sm font-bold amount-display">₹{payload[0].value.toLocaleString('en-IN')}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card-base p-4">
      <h3 className="text-sm font-semibold text-foreground mb-1">Settlement Breakdown</h3>
      <p className="text-xs text-muted-foreground mb-4">Visual distribution of lot financials</p>
      <ResponsiveContainer width="100%" height={220}>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="20%"
          outerRadius="90%"
          data={data}
          startAngle={90}
          endAngle={-270}
        >
          <RadialBar
            minAngle={15}
            dataKey="value"
            cornerRadius={6}
            background={{ fill: 'var(--muted)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconSize={8}
            iconType="circle"
            formatter={(value) => (
              <span style={{ color: 'var(--muted-foreground)', fontSize: '11px' }}>{value}</span>
            )}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="text-center mt-2">
        <p className="text-xs text-muted-foreground">Net Balance</p>
        <p className={`text-2xl font-bold amount-display ${net >= 0 ? 'text-accent' : 'text-warning'}`}>
          ₹{Math.abs(net).toLocaleString('en-IN')}
        </p>
      </div>
    </div>
  );
}